// src/shared/components/Navbar.jsx
import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu, X, ShoppingBag, Search,
  ChevronDown, User, Package, LogOut, MapPin,
} from "lucide-react";
import { useAuth }           from "@/context/AuthContext";
import { useCart }           from "@/context/CartContext";
import { NAV_LINKS }         from "@/config/navigation";
import { useProductSearch }  from "@/features/search/useProductSearch";
import { SearchDropdown }    from "@/features/search/SearchDropdown";
import { SearchOverlay }     from "@/features/search/SearchOverlay";

export default function Navbar() {
  const [open,          setOpen]          = useState(false);
  const [userMenuOpen,  setUserMenuOpen]  = useState(false);
  const [trackOpen,     setTrackOpen]     = useState(false);
  const [trackInput,    setTrackInput]    = useState("");

  const { user, logout } = useAuth();
  const { itemCount }    = useCart();
  const navigate         = useNavigate();
  const userMenuRef      = useRef(null);
  const trackInputRef    = useRef(null);

  // ── Global search ──────────────────────────────────────────────────────────
  const search = useProductSearch();

  // Close mobile drawer when overlay opens (keeps UI clean)
  const handleSearchIconClick = useCallback(() => {
    setOpen(false);
    search.open();
  }, [search]);

  // ── Auth helpers ───────────────────────────────────────────────────────────
  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate("/");
      setOpen(false);
      setUserMenuOpen(false);
    } catch {
      alert("Logout failed. Try again.");
    }
  }, [logout, navigate]);

  // Close user dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus track input when modal opens
  useEffect(() => {
    if (trackOpen) {
      setTimeout(() => trackInputRef.current?.focus(), 50);
    }
  }, [trackOpen]);

  const openTrackModal = () => {
    setUserMenuOpen(false);
    setOpen(false);
    setTrackInput("");
    setTrackOpen(true);
  };

  const handleTrack = () => {
    const val = trackInput.trim();
    if (!val) return;
    setTrackOpen(false);
    if (/^[a-f0-9]{24}$/i.test(val)) {
      navigate(`/orders/${val}/tracking`);
    } else {
      navigate(`/orders?search=${encodeURIComponent(val)}`);
    }
  };

  return (
    <>
      {/* ── Main nav bar ────────────────────────────────────────────────────── */}
      <nav
        className="fixed top-0 w-full z-50 glass border-b border-stone-grey/20"
        style={{ transition: "top 0.2s ease" }}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex justify-between items-center h-16 sm:h-20">

          {/* Brand */}
          <Link
            to="/"
            className="font-playfair text-xl sm:text-2xl md:text-3xl text-void-black
                       hover:opacity-80 transition-opacity flex flex-col leading-tight"
          >
            <span className="font-semibold">MS Gawher Ali</span>
            <span className="text-[0.65rem] md:text-xs tracking-[0.3em] font-manrope font-medium gold-text">
              JEWELLERY
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-sm font-manrope font-medium text-void-black/70
                           hover:text-antique-gold transition-colors duration-200 tracking-wide"
                onClick={() => setOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1 md:gap-2">

            {/* Search icon — visible on ALL screen sizes */}
            <button
              onClick={handleSearchIconClick}
              aria-label="Search"
              className="p-2 text-void-black/70 hover:text-antique-gold transition-colors"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Cart */}
            <Link
              to="/cart"
              aria-label="Cart"
              className="relative p-2 text-void-black/70 hover:text-antique-gold transition-colors"
            >
              <ShoppingBag className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] text-[10px]
                                 bg-antique-gold text-void-black rounded-full flex items-center
                                 justify-center font-bold px-1">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* User menu (desktop only) */}
            {user ? (
              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="flex items-center gap-1.5 text-sm font-manrope font-medium px-4 py-2
                             border border-antique-gold text-antique-gold hover:bg-antique-gold
                             hover:text-void-black transition-all duration-200 rounded-sm"
                >
                  <User className="w-4 h-4" />
                  <span className="max-w-[120px] truncate">{user.full_name ?? user.email}</span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                      userMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-100
                                  rounded-lg shadow-lg py-1 z-50">
                    <Link
                      to="/account"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <User className="w-4 h-4 text-gray-400" /> My Account
                    </Link>
                    <Link
                      to="/orders"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Package className="w-4 h-4 text-gray-400" /> My Orders
                    </Link>
                    <button
                      onClick={openTrackModal}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm
                                 text-[#B8860B] hover:bg-amber-50"
                    >
                      <MapPin className="w-4 h-4 text-[#D4AF37]" /> Track Order
                    </button>
                    <hr className="my-1 border-gray-100" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm
                                 text-red-500 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden md:block text-sm font-manrope font-medium px-5 py-2
                           border border-antique-gold text-antique-gold hover:bg-antique-gold
                           hover:text-void-black transition-all duration-200 rounded-sm"
              >
                Login
              </Link>
            )}

            {/* Mobile hamburger */}
            <button
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              className="lg:hidden p-2 text-void-black/70 hover:text-antique-gold transition-colors"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Desktop search dropdown (hidden on mobile) ─────────────────────── */}
      <SearchDropdown {...search} />

      {/* ── Mobile search overlay (hidden on desktop) ──────────────────────── */}
      <SearchOverlay {...search} />

      {/* ── Mobile drawer ──────────────────────────────────────────────────── */}
      <div
        style={{ top: "var(--navbar-h)", height: "calc(100dvh - var(--navbar-h))" }}
        className={`fixed left-0 w-full bg-white z-40 transform transition-transform
                    duration-300 ease-in-out overflow-y-auto lg:hidden ${
                      open ? "translate-x-0" : "-translate-x-full"
                    }`}
      >
        <div className="flex flex-col p-4 sm:p-6 space-y-6">

          {/* Mobile nav links */}
          <div className="flex flex-col space-y-4">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setOpen(false)}
                className="text-lg font-manrope font-medium text-void-black hover:text-antique-gold
                           transition-colors py-2 border-b border-stone-grey/30"
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile auth */}
          <div className="pt-4 border-t border-stone-grey/30 space-y-2">
            {user ? (
              <>
                <p className="text-xs text-void-black/40 font-manrope tracking-wide px-1 mb-3 truncate">
                  {user.full_name ?? user.email}
                </p>
                <Link
                  to="/account"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 text-base font-manrope font-medium text-void-black
                             hover:text-antique-gold transition-colors py-2.5 border-b border-stone-grey/20"
                >
                  <User className="w-4 h-4" /> My Account
                </Link>
                <Link
                  to="/orders"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 text-base font-manrope font-medium text-void-black
                             hover:text-antique-gold transition-colors py-2.5 border-b border-stone-grey/20"
                >
                  <Package className="w-4 h-4" /> My Orders
                </Link>
                <button
                  onClick={openTrackModal}
                  className="flex items-center gap-3 w-full text-base font-manrope font-medium
                             text-[#B8860B] hover:text-[#D4AF37] transition-colors
                             py-2.5 border-b border-stone-grey/20"
                >
                  <MapPin className="w-4 h-4" /> Track Order
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full text-base font-manrope font-medium
                             text-red-500 hover:text-red-600 transition-colors py-2.5 mt-1"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="block text-center w-full text-base font-manrope font-medium px-6 py-3
                           border-2 border-antique-gold text-antique-gold hover:bg-antique-gold
                           hover:text-void-black transition-all duration-200 rounded-sm"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ top: "var(--navbar-h)" }}
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
        />
      )}

      {/* ── Track Order Modal ───────────────────────────────────────────────── */}
      {trackOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-32 px-4"
          onClick={() => setTrackOpen(false)}
        >
          <div
            className="w-full max-w-md bg-white rounded-sm shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />

            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <MapPin size={16} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h2 className="font-playfair text-lg text-[#2C2C2C] leading-tight">
                      Track Your Order
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Enter your order ID or order number
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setTrackOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="flex gap-2">
                <input
                  ref={trackInputRef}
                  type="text"
                  value={trackInput}
                  onChange={(e) => setTrackInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleTrack()}
                  placeholder="e.g. ORD-2026-000012"
                  className="flex-1 px-4 py-3 text-sm border border-[#E8E0D0] rounded-sm
                             focus:outline-none focus:border-[#D4AF37] font-manrope placeholder-gray-300"
                />
                <button
                  onClick={handleTrack}
                  disabled={!trackInput.trim()}
                  className="px-5 py-3 bg-[#D4AF37] hover:bg-[#B8960B] disabled:opacity-40
                             text-white text-sm font-manrope font-medium rounded-sm
                             transition-colors whitespace-nowrap"
                >
                  Track
                </button>
              </div>

              <p className="text-xs text-gray-400 mt-4 text-center">
                Or{" "}
                <button
                  onClick={() => { setTrackOpen(false); navigate("/orders"); }}
                  className="text-[#B8860B] hover:underline"
                >
                  view all your orders
                </button>
                {" "}to track from there
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}